<template>
  <v-card class="mx-auto my-4" max-width="400" outlined>
    <!-- Image Carousel with Badge -->
    <div style="position: relative">
      <!-- Image Carousel -->
      <v-carousel
        height="250"
        :show-arrows="productProps.product.images.length > 1"
        hide-delimiters
      >
        <v-carousel-item
          v-for="(image, index) in productProps.product.images"
          :key="index"
        >
          <!-- v-img with Loader -->
          <v-img
            :src="image.src"
            :title="image.alt"
            cover
            class="carousel-image"
          >
            <!-- Loader Slot -->
            <template #placeholder>
              <v-skeleton-loader
                type="image"
                class="fill-height"
              ></v-skeleton-loader>
            </template>
          </v-img>
        </v-carousel-item>
      </v-carousel>
      <!-- Image Count Badge Positioned at Top-Right -->
      <div style="position: absolute; top: 8px; right: 8px">
        <!-- Image Count Badge with Tooltip -->
        <v-tooltip location="top">
          <template #activator="{ props }">
            <v-badge
              v-if="productProps.product.images.length > 1"
              :content="productProps.product.images.length"
              color="primary"
              overlap
              v-bind="props"
            >
              <v-icon color="white">mdi-image-multiple</v-icon>
            </v-badge>
          </template>
          <span>{{ productProps.product.images.length }} Images</span>
        </v-tooltip>
      </div>
    </div>
    <!-- Product Name with Open Link Icon -->
    <v-card-title class="py-2 d-flex align-center justify-space-between">
      <span class="text-h6 font-weight-medium">
        {{ productProps.product.name }}
      </span>
      <!-- Open Link Icon -->
      <v-tooltip location="top">
        <template #activator="{ props }">
          <v-btn
            icon
            variant="text"
            color="primary"
            v-bind="props"
            @click="openProductUrl"
          >
            <v-icon>mdi-open-in-new</v-icon>
          </v-btn>
        </template>
        <span>Open Product Page</span>
      </v-tooltip>
    </v-card-title>

    <v-card-text class="specifications-inline">
      <!-- Price with Offer Icon -->
      <div class="price-section">
        <span class="price">{{ currencySymbol }}{{ productProps.product.price.price }}</span>
        <v-tooltip v-if="productProps.product.price.onOffer" location="top">
          <template #activator="{ props }">
            <v-icon left color="red" v-bind="props" class="offer-icon"
              >mdi-tag</v-icon
            >
          </template>
          <span>On Offer</span>
        </v-tooltip>
      </div>

      <div class="specifications-icons">
        <!-- Suitable For -->
        <v-tooltip location="top">
          <template #activator="{ props }">
            <div class="spec-item" v-bind="props">
              <v-icon class="spec-icon">mdi-sofa</v-icon>
              <span>{{ productProps.product.specification.suitableFor }}</span>
            </div>
          </template>
          <span>Suitable For</span>
        </v-tooltip>
        <!-- Size -->
        <v-tooltip location="top">
          <template #activator="{ props }">
            <div class="spec-item" v-bind="props">
              <v-icon class="spec-icon">mdi-ruler-square</v-icon>
              <span>{{ productProps.product.specification.size }}</span>
            </div>
          </template>
          <span>Size</span>
        </v-tooltip>
        <!-- Required Space -->
        <v-tooltip location="top">
          <template #activator="{ props }">
            <div class="spec-item" v-bind="props">
              <v-icon class="spec-icon">mdi-vector-square</v-icon>
              <span>{{
                productProps.product.specification.requiredSpace
              }}</span>
            </div>
          </template>
          <span>Required Space</span>
        </v-tooltip>
        <!-- Main Category -->
        <v-tooltip location="top">
          <template #activator="{ props }">
            <div class="spec-item" v-bind="props">
              <v-icon class="spec-icon">mdi-folder-outline</v-icon>
              <span>{{ mainCategoryName }}</span>
            </div>
          </template>
          <span>Main Category</span>
        </v-tooltip>
        <!-- Categories Badge with Tooltip -->
        <v-tooltip v-if="categoryNames.length > 1" location="top">
          <template #activator="{ props }">
            <v-badge
              :content="categoryNames.length"
              overlap
              v-bind="props"
              class="category-badge"
            >
              <v-icon class="spec-icon">mdi-folder-multiple-outline</v-icon>
            </v-badge>
          </template>
          <span>Categories: {{ categoryNames.join(', ') }}</span>
        </v-tooltip>
      </div>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { useCategoriesStore } from '@/store/categoriesStore';
  import { useActiveUrlStore } from '@/store/activeUrlStore';
  import IProduct from '@/interfaces/IProduct';
  import Provider from '@/enums/Provider';
  import { getPaddedBackgroundImageUrl } from '@/helpers/getPaddedBackgroundImageUrl';

  const productProps = defineProps<{
    product: IProduct;
  }>();

  const categoriesStore = useCategoriesStore();
  const activeUrlStore = useActiveUrlStore();

  // Get the names of categories the product belongs to using the store method
  const categoryNames = computed(() =>
  categoriesStore.getCategoryNamesByProductPath(productProps.product.pathname)
  );

  // Computed property for main category name
  const mainCategoryName = computed(() =>
  categoriesStore.getNameByPathname(productProps.product.mainCategory)
  );

  // Method to open the product URL in a new tab
  function openProductUrl() {
    const url = activeUrlStore.getUrlByPathname(productProps.product.pathname);
    window.open(url, '_blank');
  }

  const provider = activeUrlStore.getProvider;

  // Computed property for currency symbol based on provider
  const currencySymbol = computed(() => {
    if (provider === Provider.KBS) {
      return '£';
    } else if (provider === Provider.ERS || provider === Provider.IO) {
      return '$';
    } else {
      return ''; // Default symbol if provider doesn't match any case
    }
  });
</script>

<style scoped>
  .v-card {
    transition: transform 0.2s;
  }
  .v-card:hover {
    transform: scale(1.02);
  }
  .carousel-image {
    height: 250px;
  }
  .fill-height {
    height: 100%;
  }
  .specifications-inline {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .price-section {
    display: flex;
    align-items: center;
    font-size: 16px;
    font-weight: bold;
  }
  .price {
    margin-right: 8px;
  }
  .offer-icon {
    margin-left: 8px;
  }
  .specifications-icons {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: center;
  }
  .spec-item {
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .spec-icon {
    color: #757575;
  }
</style>
